import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { playAlarm, toggleFullscreen } from "@/lib/tv";
import {
  CornersOut, SignOut, SpeakerHigh, SpeakerSlash, WarningDiamond, ShieldCheck,
} from "@phosphor-icons/react";

// Dedicated zero-chrome TV kiosk. Auto-starts, stays awake, scoped to the
// logged-in store. Meant to run fullscreen on an office TV (Fire Stick etc.).
export default function TvKiosk() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const camParam = params.get("cam");
  const cycle = params.get("mode") === "cycle" || location.pathname === "/cam";
  const liveOnly = location.pathname === "/live";
  const isMaster = user?.role === "master" || user?.role === "admin";
  const storeLabel = isMaster ? "ALL SITES" : ((user?.store_names || []).join(" · ") || "STORE");

  const [cams, setCams] = useState([]);
  const targetCams = useMemo(
    () => (camParam ? cams.filter((c) => c.id === camParam) : cams),
    [cams, camParam]
  );
  const [tiles, setTiles] = useState({});
  const [ticker, setTicker] = useState([]);
  const [sound, setSound] = useState(true);
  const [now, setNow] = useState(new Date());
  const [flash, setFlash] = useState(null);
  const [showChrome, setShowChrome] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [activity, setActivity] = useState("idle"); // idle | active | hyper
  const [remain, setRemain] = useState(0);

  const scanningRef = useRef(true);
  const soundRef = useRef(true);
  const seenRef = useRef(new Set());
  const freshRef = useRef({ ts: null, at: Date.now() });
  soundRef.current = sound;

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  // Keep the TV screen awake.
  useEffect(() => {
    let lock;
    const acquire = async () => { try { lock = await navigator.wakeLock?.request("screen"); } catch { /* unsupported */ } };
    acquire();
    const onVis = () => { if (document.visibilityState === "visible") acquire(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { document.removeEventListener("visibilitychange", onVis); try { lock && lock.release(); } catch { /* noop */ } };
  }, []);

  // Auto-hide the floating controls after a few seconds of no interaction.
  useEffect(() => {
    if (!showChrome) return;
    const t = setTimeout(() => setShowChrome(false), 4000);
    return () => clearTimeout(t);
  }, [showChrome]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cameras");
      setCams(data.filter((c) => c.status === "online"));
    } catch { /* retry next cycle */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Fast live-frame poller (~0.7s) for the kiosk grid.
  useEffect(() => {
    if (cycle || targetCams.length === 0) return;
    let cancelled = false;
    const pull = async () => {
      await Promise.all(targetCams.map(async (cam) => {
        try {
          const r = await api.get(`/cameras/${cam.id}/frame?display=1`);
          if (!cancelled && r.data?.b64) {
            setTiles((p) => ({ ...p, [cam.id]: { ...(p[cam.id] || {}), frame: r.data.b64 } }));
            if (r.data.ts && r.data.ts !== freshRef.current.ts) freshRef.current = { ts: r.data.ts, at: Date.now() };
          }
        } catch { /* no frame yet */ }
      }));
    };
    pull();
    const t = setInterval(pull, liveOnly ? 300 : 700);
    return () => { cancelled = true; clearInterval(t); };
  }, [targetCams]);

  // Self-heal: if frames stop advancing for 60s (stalled TV timer / dropped
  // token / network blip), reload the whole page so the feed recovers on its own.
  useEffect(() => {
    const t = setInterval(() => {
      if (targetCams.length && Date.now() - freshRef.current.at > 60000) {
        window.location.reload();
      }
    }, 15000);
    return () => clearInterval(t);
  }, [targetCams]);

  // Auto AI scan loop (self-training + alerts), no button needed.
  useEffect(() => {
    if (cycle || liveOnly || targetCams.length === 0) return;
    let cancelled = false;
    let timer;
    const scanCam = async (cam) => {
      try {
        const { data } = await api.post("/monitor/scan", {
          camera_id: cam.id, motion_only: false, motion_threshold: 0,
          theft_threshold: 65, vandalism_threshold: 65, effectiveness_min: 40,
        });
        const sev = data.alerts?.[0]?.severity;
        setTiles((p) => ({ ...p, [cam.id]: { ...(p[cam.id] || {}), result: data.result, status: data.status, sev, alerts: data.alerts || [] } }));
        if (data.alerts?.length) {
          setTicker((p) => [...data.alerts, ...p].slice(0, 40));
          data.alerts.forEach((a) => {
            if (seenRef.current.has(a.id)) return;
            seenRef.current.add(a.id);
            const hard = a.severity === "critical" || a.severity === "high";
            if (hard) {
              if (soundRef.current) playAlarm(a.severity);
              setFlash({ id: a.id, type: a.type, camera: a.camera, message: a.message, severity: a.severity });
              setTimeout(() => setFlash((f) => (f && f.id === a.id ? null : f)), 8000);
            }
          });
        }
      } catch { /* skip this camera this cycle */ }
    };
    const loop = async () => {
      for (const cam of targetCams) { if (cancelled) return; await scanCam(cam); }
      if (!cancelled) timer = setTimeout(loop, 25000);
    };
    loop();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [targetCams]);

  const n = targetCams.length;
  const cols = n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : 4;
  const clock = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  const flashHard = flash?.severity === "critical";
  const streamUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/cameras/${id}/stream?token=${localStorage.getItem("sv_token")}`;

  // Rotation mode: one camera at a time. Dwell ~20s; skip quiet cameras sooner;
  // LOCK onto a camera while it shows hyperactivity (alert / crowd / high risk).
  useEffect(() => {
    if (!cycle || targetCams.length === 0) return;
    let cancelled = false, frameTimer, tickTimer;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    const fetchFrame = async (cam) => {
      try {
        const r = await api.get(`/cameras/${cam.id}/frame?display=1`);
        if (!cancelled && r.data?.b64) {
          setTiles((p) => ({ ...p, [cam.id]: { ...(p[cam.id] || {}), frame: r.data.b64 } }));
          if (r.data.ts && r.data.ts !== freshRef.current.ts) freshRef.current = { ts: r.data.ts, at: Date.now() };
        }
      } catch { /* noop */ }
    };
    const scan = async (cam) => {
      try {
        const { data } = await api.post("/monitor/scan", {
          camera_id: cam.id, motion_only: false, motion_threshold: 0,
          theft_threshold: 65, vandalism_threshold: 65, effectiveness_min: 40,
        });
        const sev = data.alerts?.[0]?.severity;
        setTiles((p) => ({ ...p, [cam.id]: { ...(p[cam.id] || {}), result: data.result, status: data.status, sev, alerts: data.alerts || [] } }));
        if (data.alerts?.length) {
          setTicker((p) => [...data.alerts, ...p].slice(0, 40));
          data.alerts.forEach((a) => {
            if (seenRef.current.has(a.id)) return;
            seenRef.current.add(a.id);
            const hard = a.severity === "critical" || a.severity === "high";
            if (hard) {
              if (soundRef.current) playAlarm(a.severity);
              setFlash({ id: a.id, type: a.type, camera: a.camera, message: a.message, severity: a.severity });
              setTimeout(() => setFlash((f) => (f && f.id === a.id ? null : f)), 8000);
            }
          });
        }
        const people = data.result?.people_count ?? 0;
        const risk = Math.max(data.result?.theft_risk?.score ?? 0, data.result?.vandalism_risk?.score ?? 0);
        if ((data.alerts?.length || 0) > 0 || people >= 3 || risk >= 65) return "hyper";
        if (people >= 1) return "active";
        return "idle";
      } catch { return "idle"; }
    };
    const run = async () => {
      let i = 0;
      while (!cancelled) {
        const idx = i % targetCams.length;
        const cam = targetCams[idx];
        setActiveIdx(idx);
        await fetchFrame(cam);
        clearInterval(frameTimer); frameTimer = setInterval(() => fetchFrame(cam), 1200);
        const level = await scan(cam);
        if (cancelled) break;
        setActivity(level);
        if (level === "hyper") {          // hyperactivity → stay locked on this camera
          setRemain(0);
          await sleep(10000);
          clearInterval(frameTimer);
          continue;                        // do NOT advance
        }
        const dwell = level === "active" ? 20000 : 8000;   // quiet cameras move on sooner
        let left = Math.round(dwell / 1000); setRemain(left);
        clearInterval(tickTimer); tickTimer = setInterval(() => { left -= 1; setRemain(left > 0 ? left : 0); }, 1000);
        await sleep(dwell);
        clearInterval(frameTimer); clearInterval(tickTimer);
        i = idx + 1;
      }
    };
    run();
    return () => { cancelled = true; clearInterval(frameTimer); clearInterval(tickTimer); };
  }, [cycle, targetCams]);

  const renderTile = (cam) => {
    const t = tiles[cam.id] || {};
    const flagged = !liveOnly && (t.alerts?.length || 0) > 0;
    return (
      <div key={cam.id} data-testid={`tv-tile-${cam.id}`}
        className={`relative bg-black overflow-hidden min-h-0 w-full h-full border ${flagged ? (t.sev === "critical" ? "border-[#FF3B30]" : "border-[#FF9F0A]") + " shadow-[0_0_0_2px_currentColor]" : "border-white/10"}`}>
        {t.frame ? (
          <img src={`data:image/jpeg;base64,${t.frame}`} alt={cam.name} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[#3f3f46] font-mono-data text-xs">connecting…</div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-[#32D74B] live-dot" />
          <span className="font-mono-data text-xs tracking-wide bg-black/60 px-2 py-0.5 text-white">{cam.name}</span>
        </div>
        {flagged && (
          <div className="absolute top-2 right-2 flex items-center gap-1 bg-[#FF3B30] px-2 py-0.5 animate-pulse">
            <WarningDiamond size={13} weight="fill" />
            <span className="font-mono-data text-xs font-bold uppercase text-white">{t.alerts[0].type}</span>
          </div>
        )}
        {!liveOnly && t.result && (
          <div className="absolute bottom-0 left-0 right-0 px-3 py-1.5 bg-gradient-to-t from-black/90 to-transparent flex gap-3 font-mono-data text-sm">
            <span style={{ color: (t.result.theft_risk?.score ?? 0) >= 65 ? "#FF3B30" : "#94A3B8" }}>T{t.result.theft_risk?.score ?? 0}</span>
            <span style={{ color: (t.result.vandalism_risk?.score ?? 0) >= 65 ? "#FF3B30" : "#94A3B8" }}>V{t.result.vandalism_risk?.score ?? 0}</span>
            <span className="text-[#94A3B8]">P{t.result.people_count ?? 0}</span>
          </div>
        )}
        <span className="absolute bottom-1.5 right-3 z-10 font-display font-bold text-xs tracking-[0.2em] uppercase text-[#0A84FF]" style={{ textShadow: "0 1px 3px #000" }}>Powered by A V Tech</span>
      </div>
    );
  };

  return (
    <div
      data-testid="tv-kiosk"
      onMouseMove={() => setShowChrome(true)}
      onClick={() => setShowChrome(true)}
      className={`fixed inset-0 bg-black flex flex-col select-none cursor-none ${flash ? (flashHard ? "ring-4 ring-[#FF3B30] ring-inset" : "ring-4 ring-[#FF9F0A] ring-inset") : ""}`}
    >
      {/* Top telemetry strip */}
      <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-b from-black/90 to-transparent z-20 pointer-events-none">
        <div className="flex items-center gap-3">
          <ShieldCheck size={26} weight="fill" className="text-[#007AFF]" />
          <div>
            <p className="font-display font-black uppercase tracking-tighter text-white text-lg leading-none">SentryVision</p>
            <p className="font-mono-data text-[11px] uppercase tracking-[0.2em] text-[#0A84FF] mt-0.5">{storeLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-5">
          <span className="flex items-center gap-2 font-mono-data text-sm text-[#32D74B]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#32D74B] live-dot" /> LIVE · {n} CAM{cycle ? " · ROTATE" : liveOnly ? " · WALL" : " · ANALYSIS"}
          </span>
          <span data-testid="tv-freshness" className="font-mono-data text-xs text-[#94A3B8]">
            updated {Math.max(0, Math.round((now - freshRef.current.at) / 1000))}s ago
          </span>
          <span data-testid="tv-clock" className="font-mono-data text-2xl md:text-3xl font-medium text-white tracking-tight tabular-nums">{clock}</span>
        </div>
      </div>

      {/* Alert spotlight banner */}
      {flash && (
        <div
          data-testid="tv-alert-banner"
          className={`absolute top-16 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-6 py-3 ${flashHard ? "bg-[#FF3B30]" : "bg-[#FF9F0A]"} text-black font-display font-black uppercase tracking-wide shadow-2xl animate-pulse`}
        >
          <WarningDiamond size={26} weight="fill" />
          <div className="leading-tight">
            <p className="text-lg">{flash.type} · {flash.camera}</p>
            <p className="text-xs font-mono-data font-bold normal-case tracking-normal">{flash.message}</p>
          </div>
        </div>
      )}

      {/* Camera matrix (grid) OR rotation (single, auto-advancing) */}
      {cycle ? (
        <div className="flex-1 min-h-0 relative p-1" data-testid="tv-grid">
          {targetCams.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center text-[#3f3f46] font-mono-data text-sm">Waiting for cameras…</div>
          ) : (
            <div className="w-full h-full relative">
              {renderTile(targetCams[activeIdx % targetCams.length])}
              <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
                {activity === "hyper" ? (
                  <span data-testid="tv-rotate-status" className="flex items-center gap-2 px-4 py-1.5 bg-[#FF3B30] text-black font-display font-black uppercase text-sm tracking-wide animate-pulse">
                    <WarningDiamond size={16} weight="fill" /> Locked · High Activity
                  </span>
                ) : (
                  <span data-testid="tv-rotate-status" className="px-4 py-1.5 bg-black/70 text-white font-mono-data text-xs uppercase tracking-wider">
                    {activity === "active" ? "Holding · activity" : "Auto-rotate · quiet"} · next in {remain}s
                  </span>
                )}
              </div>
              {targetCams.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 pointer-events-none">
                  {targetCams.map((c, idx) => (
                    <span key={c.id} className={`h-1.5 rounded-full transition-all duration-300 ${idx === (activeIdx % targetCams.length) ? "w-6 bg-[#007AFF]" : "w-1.5 bg-white/30"}`} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid gap-1 p-1" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="tv-grid">
          {targetCams.length === 0 ? (
            <div className="flex items-center justify-center text-[#3f3f46] font-mono-data text-sm">
              Waiting for cameras… make sure the Edge Agent is running.
            </div>
          ) : (
            targetCams.map((cam) => renderTile(cam))
          )}
        </div>
      )}

      {/* Scrolling event ticker */}
      <div className="bg-[#0A0A0C] border-t border-white/20 overflow-hidden z-20">
        <div className="flex items-center">
          <span className="shrink-0 bg-[#FF3B30] text-white font-display font-black uppercase text-sm px-4 py-2 tracking-wide">Live Feed</span>
          <div className="flex-1 overflow-hidden whitespace-nowrap py-2">
            {ticker.length === 0 ? (
              <span className="px-4 font-mono-data text-sm text-[#64748B]">All clear — AI scanning &amp; self-training active. Powered by A V Tech.</span>
            ) : (
              <div className="inline-block animate-[marquee_30s_linear_infinite] whitespace-nowrap">
                {ticker.map((a) => (
                  <span key={a.id} className="mx-8 font-mono-data text-sm">
                    <span style={{ color: a.severity === "critical" || a.severity === "high" ? "#FF3B30" : "#FF9F0A" }}>▲ {a.type}</span>
                    <span className="text-[#94A3B8]"> · {a.camera} · {a.message}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <span className="shrink-0 px-4 font-display font-bold tracking-[0.25em] uppercase text-sm text-[#0A84FF]" data-testid="tv-branding">Powered by A V Tech</span>
        </div>
      </div>

      {/* Floating controls (auto-hide) */}
      <div className={`absolute bottom-16 right-4 z-40 flex items-center gap-2 transition-opacity duration-300 cursor-auto ${showChrome ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
        <button data-testid="tv-sound-toggle" onClick={() => setSound((s) => !s)} title="Toggle alarm"
          className="w-11 h-11 flex items-center justify-center bg-[#121214]/90 border border-white/20 text-white hover:border-white/40">
          {sound ? <SpeakerHigh size={20} className="text-[#32D74B]" /> : <SpeakerSlash size={20} className="text-[#71717A]" />}
        </button>
        <button data-testid="tv-fullscreen" onClick={() => toggleFullscreen(document.documentElement)} title="Fullscreen"
          className="flex items-center gap-2 h-11 px-4 bg-[#007AFF] hover:bg-[#3395FF] text-white font-display font-bold uppercase text-xs tracking-wide">
          <CornersOut size={18} weight="bold" /> Fullscreen
        </button>
        <button data-testid="tv-exit" onClick={() => navigate("/wall")} title="Exit TV mode"
          className="w-11 h-11 flex items-center justify-center bg-[#121214]/90 border border-white/20 text-[#A1A1AA] hover:text-[#FF3B30] hover:border-[#FF3B30]/40">
          <SignOut size={20} />
        </button>
      </div>
    </div>
  );
}
